import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ConfirmDialog,
  type ContextMenuItem,
  MediaCard,
  toast,
} from '@core/components';
import { useConfirmDialog } from '@core/hooks/useConfirmDialog';
import { useCrudList } from '@core/hooks/useCrudList';
import { type Collar, deleteCollar, reorderCollars } from '../../../api/collar';
import {
  type Vestment,
  getAllVestments,
  reorderVestments,
} from '../../../api/vestment';
import CollarDialog from '../../components/CollarDialog';
import GroupedSortableSettings, {
  useSelectedGroup,
} from '../../components/GroupedSortableSettings';
import VestmentDialog from '../../components/VestmentDialog';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { getCategory } from '../../enum/vestmentType';
import { getChasubleFeatures } from '../../utils/chasuble';
import { isRequiredCollar } from '../../utils/collar';
import { getImageAspectRatio, getImageThumbnailUrl } from '../../utils/image';

const Chasubles = () => {
  const { refreshContext } = useAppContext();
  const [imageDialog, setImageDialog] = useState<{
    open: boolean;
    vestment: Vestment | null;
  }>({ open: false, vestment: null });
  const [collarDialog, setCollarDialog] = useState<{
    open: boolean;
    collar: Collar | null;
  }>({ open: false, collar: null });

  const loadChasubles = useCallback(
    async () =>
      (await getAllVestments()).filter(
        (vestment) =>
          !vestment.isHidden &&
          getCategory(vestment.type) === VestmentCategory.Chasuble
      ),
    []
  );
  const reorderChasubles = useCallback(
    (ids: number[]) => reorderVestments(VestmentCategory.Chasuble, ids),
    []
  );

  const {
    items: chasubles,
    setItems: setChasubles,
    loading,
    load,
    rowReorder,
  } = useCrudList<Vestment>({
    loadItems: loadChasubles,
    reorderItems: reorderChasubles,
    loadErrorMessage: 'Failed to load chasubles',
    reorderErrorMessage: 'Failed to reorder chasubles',
    refreshContext,
  });

  const { selectedGroup: selectedChasuble, selectGroup: selectChasuble } =
    useSelectedGroup(chasubles, 'chasuble');
  const allowsCollars =
    getChasubleFeatures(selectedChasuble?.type)?.collar ?? false;

  const collarDelete = useConfirmDialog<Collar>({
    onConfirm: (collar) => deleteCollar(collar.id),
    onSuccess: load,
    successMessage: 'Collar deleted',
    errorMessage: 'Failed to delete collar',
  });

  const collarMenuItems = (collar: Collar): ContextMenuItem[] => [
    { label: 'Edit', onClick: () => setCollarDialog({ open: true, collar }) },
    ...(selectedChasuble && isRequiredCollar(collar, selectedChasuble.type)
      ? []
      : [
          {
            label: 'Delete',
            destructive: true,
            onClick: () => collarDelete.request(collar),
          },
        ]),
  ];

  const onCollarsReorder = (chasuble: Vestment, next: Collar[]) => {
    setChasubles((prev) =>
      prev.map((item) =>
        item.id === chasuble.id ? { ...item, collars: next } : item
      )
    );
    reorderCollars(
      chasuble.id,
      next.map((collar) => collar.id)
    )
      .then(refreshContext)
      .catch(() => {
        toast.error('Failed to reorder collars');
        void load();
      });
  };

  return (
    <GroupedSortableSettings<Vestment, Collar>
      title="Chasubles"
      loading={loading}
      groups={chasubles}
      selectedGroup={selectedChasuble}
      onSelectGroup={selectChasuble}
      groupReorder={rowReorder}
      groupLabel="Chasuble"
      getGroupMenuItems={(vestment) => [
        {
          label: 'Edit',
          onClick: () => setImageDialog({ open: true, vestment }),
        },
      ]}
      emptyState={{ description: 'No chasubles are configured.' }}
      getItems={(chasuble) => chasuble.collars}
      getItemsCaption={(_, count) => {
        if (!allowsCollars) {
          return 'This chasuble type has no collars.';
        }
        return count === 0
          ? 'No collars yet.'
          : `${count} collar${count === 1 ? '' : 's'}`;
      }}
      onItemsReorder={onCollarsReorder}
      renderItem={(collar, onMenuClick) => (
        <MediaCard
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" textAlign="center">
                {collar.name}
              </Typography>
            </Box>
          }
          imageSrc={getImageThumbnailUrl(collar)}
          aspectRatio={getImageAspectRatio(collar) ?? 1}
          fullHeight
          onClick={() => setCollarDialog({ open: true, collar })}
          onMenuClick={onMenuClick}
        />
      )}
      getItemMenuItems={collarMenuItems}
      addItem={
        allowsCollars
          ? {
              label: 'Add collar',
              onClick: () => setCollarDialog({ open: true, collar: null }),
            }
          : undefined
      }
    >
      {imageDialog.vestment ? (
        <VestmentDialog
          open={imageDialog.open}
          onClose={() =>
            setImageDialog((current) => ({ ...current, open: false }))
          }
          vestment={imageDialog.vestment}
          onSuccess={() => void load()}
        />
      ) : null}
      {selectedChasuble ? (
        <CollarDialog
          open={collarDialog.open}
          onClose={() => setCollarDialog({ open: false, collar: null })}
          vestment={selectedChasuble}
          collar={collarDialog.collar}
          onSuccess={() => void load()}
        />
      ) : null}
      <ConfirmDialog
        open={collarDelete.open}
        title="Delete Collar"
        content={`Are you sure you want to delete "${collarDelete.item?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={() => void collarDelete.confirm()}
        onCancel={collarDelete.cancel}
        loading={collarDelete.loading}
      />
    </GroupedSortableSettings>
  );
};

export default Chasubles;
